/**
 * Where a desktop Dropdown panel goes, relative to its trigger.
 *
 * The panel opens below its trigger when it fits there, above when it fits
 * there instead, and otherwise on whichever side has more room — capped to that
 * room so it scrolls rather than running off the screen. The old rule flipped
 * above whenever it did not fit below, even when there was less room above, so
 * a tall panel (the dashboard Filters panel) opened with its top off-screen and
 * could not be reached.
 *
 * Everything is in viewport coordinates: the panel is `position: fixed`.
 */

/** Breathing room kept between the panel and the edge of the viewport. */
export const VIEWPORT_MARGIN = 8;

export interface Rect {
  top: number;
  bottom: number;
  left: number;
  width: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Placement {
  top: number;
  left: number;
  /** Set only when the panel fits on neither side and has to scroll. */
  maxHeight: number | null;
}

export const placeDropdown = (trigger: Rect, panel: Size, viewport: Size): Placement => {
  const spaceBelow = viewport.height - trigger.bottom - VIEWPORT_MARGIN;
  const spaceAbove = trigger.top - VIEWPORT_MARGIN;

  let top: number;
  let maxHeight: number | null = null;

  if (panel.height <= spaceBelow) {
    top = trigger.bottom;
  } else if (panel.height <= spaceAbove) {
    top = trigger.top - panel.height;
  } else if (spaceBelow >= spaceAbove) {
    top = trigger.bottom;
    maxHeight = Math.max(spaceBelow, 0);
  } else {
    top = VIEWPORT_MARGIN;
    maxHeight = spaceAbove;
  }

  // Right-aligned to the trigger when there is room to its left, otherwise
  // left-aligned — then kept inside the viewport either way.
  let left =
    trigger.left >= panel.width ? trigger.left + trigger.width - panel.width : trigger.left;
  const maxLeft = viewport.width - panel.width - VIEWPORT_MARGIN;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

  return { top, left, maxHeight };
};
